/** @format */

import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { Alert, Button, Drawer, Input, Segmented, Skeleton } from "antd";
import { saveAs } from "file-saver";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import {
  apiIntermittentAction,
  apiIntermittentDetail,
  apiIntermittentUpdateInpsCode,
  apiIntermittentXml,
  intermittentErrorMessage,
  intermittentValidationMessage,
} from "../../../../api/apiIntermittentCommunications";
import {
  TIntermittentAction,
  TIntermittentCommunicationDetail,
  TIntermittentEvent,
  TIntermittentShiftLink,
} from "../../../../redux-modules/declarations/maps/intermittentCommunications";
import { Routes } from "../../../../utils/Routes";
import { showNotification } from "../../../../utils/showNotification";
import {
  ACTION_META,
  ACTION_ORDER,
  DETACH_REASONS,
  EVENT_LABELS,
  fmtDateTime,
  fmtDay,
  fullName,
  hoursFromNow,
  inHoursLabel,
  isOvernight,
  nextStep,
  STATUS_META,
} from "../constants";
import { maiuscola, orario, TONO_STATO, TTono } from "../format";
import { Corpo, Piede, Pillole, Titolo } from "../styles";
import { PillolaStato, PilloleTipologia } from "./Pillole";

interface Props {
  /** comunicazione aperta; null = pannello chiuso */
  id: number | null;
  /** destinatari configurati per gli XML (riepilogo della pagina) */
  destinatariXml?: string[];
  onChiudi: () => void;
  /** un'azione o il codice INPS hanno cambiato i dati: rilegge elenco, riepilogo e menu */
  onModificata: () => void;
}

type TScheda = "cronologia" | "turni" | "xml";

/** Riquadro in evidenza: cosa sta succedendo alla comunicazione e cosa fare. */
interface TRiquadro {
  tono: TTono;
  titolo: string;
  testo: React.ReactNode;
}

/** Dettaglio di una comunicazione: stessa struttura del dettaglio turno della vista settimanale. */
export const DettaglioComunicazione = ({
  id,
  destinatariXml,
  onChiudi,
  onModificata,
}: Props): JSX.Element => {
  const history = useHistory();
  const [dettaglio, setDettaglio] = useState<TIntermittentCommunicationDetail | null>(null);
  const [caricamento, setCaricamento] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [lettura, setLettura] = useState(0);
  const [scheda, setScheda] = useState<TScheda>("cronologia");
  // conferma in linea: un'azione con la sua nota, oppure il codice INPS
  const [azione, setAzione] = useState<TIntermittentAction | null>(null);
  const [nota, setNota] = useState("");
  const [codiceInps, setCodiceInps] = useState<string | null>(null);
  const [erroreModulo, setErroreModulo] = useState<string | null>(null);
  const [salvataggio, setSalvataggio] = useState(false);

  useEffect(() => {
    setScheda("cronologia");
    setAzione(null);
    setCodiceInps(null);
    setErroreModulo(null);
    if (!id) {
      setDettaglio(null);
      return;
    }
    let annullata = false;
    setCaricamento(true);
    setErrore(null);
    apiIntermittentDetail(id)
      .then(d => {
        if (!annullata) setDettaglio(d);
      })
      .catch(err => {
        if (!annullata) {
          setErrore(intermittentErrorMessage(err, "Impossibile caricare il dettaglio."));
        }
      })
      .finally(() => {
        if (!annullata) setCaricamento(false);
      });
    return (): void => {
      annullata = true;
    };
  }, [id, lettura]);

  const d = dettaglio && dettaglio.id === id ? dettaglio : null;
  const azioni = d ? ACTION_ORDER.filter(a => d.available_actions.indexOf(a) >= 0) : [];
  const modulo = azione !== null || codiceInps !== null;

  const apriAzione = (a: TIntermittentAction): void => {
    setCodiceInps(null);
    setNota("");
    setErroreModulo(null);
    setAzione(a);
  };

  const apriCodiceInps = (): void => {
    if (!d) return;
    setAzione(null);
    setErroreModulo(null);
    setCodiceInps(d.inps_code || "");
  };

  const chiudiModulo = (): void => {
    setAzione(null);
    setCodiceInps(null);
    setErroreModulo(null);
  };

  const eseguiAzione = (): void => {
    if (!d || !azione) return;
    const meta = ACTION_META[azione];
    const testo = nota.trim();
    if (meta.noteRequired && !testo) {
      setErroreModulo("La nota è obbligatoria");
      return;
    }
    setSalvataggio(true);
    apiIntermittentAction(d.id, azione, testo || undefined)
      .then(aggiornata => {
        showNotification(
          "success",
          meta.label,
          `${fullName(aggiornata)} – ${fmtDay(aggiornata.work_date)}: ${
            STATUS_META[aggiornata.status]?.label || aggiornata.status_label
          }`,
        );
        setDettaglio(aggiornata);
        chiudiModulo();
        onModificata();
      })
      .catch(err =>
        showNotification("error", meta.label, intermittentErrorMessage(err, "Operazione non riuscita.")),
      )
      .finally(() => setSalvataggio(false));
  };

  const salvaCodiceInps = (): void => {
    if (!d || codiceInps === null) return;
    const codice = codiceInps.replace(/\s+/g, "");
    if (!codice) return setErroreModulo("Inserisci il codice comunicazione INPS");
    if (!/^[A-Za-z0-9]+$/.test(codice)) {
      return setErroreModulo("Il codice può contenere solo lettere e numeri");
    }
    if (codice.length > 30) return setErroreModulo("Massimo 30 caratteri");
    setSalvataggio(true);
    apiIntermittentUpdateInpsCode(d.id, codice)
      .then(aggiornata => {
        showNotification(
          "success",
          "Codice INPS salvato",
          `${fullName(aggiornata)}: codice salvato in anagrafica${
            d.status === "blocked" && aggiornata.status !== "blocked"
              ? ", comunicazione sbloccata"
              : ""
          }.`,
        );
        setDettaglio(aggiornata);
        chiudiModulo();
        onModificata();
      })
      .catch(err =>
        setErroreModulo(
          intermittentValidationMessage(err, "Impossibile salvare il codice INPS."),
        ),
      )
      .finally(() => setSalvataggio(false));
  };

  const turnoIniziato = !!d && moment(d.shift_start_at).isSameOrBefore(moment());

  const titolo = d ? (
    <Titolo>
      <span className="nome">{fullName(d)}</span>
      <span className="quando">
        {maiuscola(moment(d.work_date).format("dddd D MMMM YYYY"))} · turno {orario(d)}
        {isOvernight(d) ? " (+1)" : ""}
      </span>
    </Titolo>
  ) : (
    "Comunicazione"
  );

  const piede =
    d && (azioni.length > 0 || turnoIniziato) ? (
      <Piede>
        {azioni.map(a => (
          <Button
            key={a}
            type={ACTION_META[a].primary ? "primary" : "default"}
            danger={ACTION_META[a].danger}
            disabled={salvataggio || modulo}
            onClick={(): void => apriAzione(a)}
            data-cy={`intermittent-action-${a}`}
          >
            {ACTION_META[a].label}
          </Button>
        ))}
        {turnoIniziato && (
          <Button
            type="link"
            onClick={(): void =>
              history.push(
                `${Routes.presences}?vista=giornata&data=${d.work_date}&pannello=${d.user_id}`,
              )
            }
          >
            Apri le Presenze
          </Button>
        )}
      </Piede>
    ) : null;

  return (
    <Drawer
      visible={!!id}
      placement="right"
      width={540}
      onClose={onChiudi}
      title={titolo}
      extra={d ? <PillolaStatoDrawer d={d} /> : undefined}
      closeIcon={<CloseOutlined aria-label="Chiudi" />}
      footer={piede}
      bodyStyle={{ padding: "16px 18px 20px" }}
    >
      {errore && !d && (
        <Alert
          type="error"
          showIcon
          message={errore}
          action={
            <Button size="small" onClick={(): void => setLettura(n => n + 1)}>
              Riprova
            </Button>
          }
        />
      )}
      {!d && !errore && <Skeleton active />}
      {d && (
        <Corpo aria-busy={caricamento}>
          <Riquadro d={d} onCodiceInps={apriCodiceInps} disabilitato={modulo || salvataggio} />

          {azione && (
            <section
              className={`conferma${ACTION_META[azione].danger ? " pericolo" : ""}`}
              aria-label={ACTION_META[azione].title}
            >
              <h3>{ACTION_META[azione].title}</h3>
              <p>{ACTION_META[azione].description}</p>
              <label htmlFor="nota-azione">
                {ACTION_META[azione].noteRequired ? "Nota" : "Nota (facoltativa)"}
              </label>
              <Input.TextArea
                id="nota-azione"
                autoFocus
                value={nota}
                maxLength={2000}
                autoSize={{ minRows: 2, maxRows: 6 }}
                placeholder={ACTION_META[azione].notePlaceholder || "Aggiungi una nota"}
                onChange={(e): void => {
                  setNota(e.target.value);
                  setErroreModulo(null);
                }}
              />
              {erroreModulo && <span className="errore">{erroreModulo}</span>}
              <div className="pulsanti">
                <Button
                  type="primary"
                  danger={ACTION_META[azione].danger}
                  loading={salvataggio}
                  onClick={eseguiAzione}
                  data-cy="intermittent-action-confirm"
                >
                  {ACTION_META[azione].okText}
                </Button>
                <Button disabled={salvataggio} onClick={chiudiModulo}>
                  Indietro
                </Button>
              </div>
            </section>
          )}

          {codiceInps !== null && (
            <section className="conferma" aria-label="Codice comunicazione INPS">
              <h3>
                {d.inps_code
                  ? "Modifica codice comunicazione INPS"
                  : "Inserisci codice comunicazione INPS"}
              </h3>
              <p>
                Il codice viene salvato nell&apos;<strong>anagrafica del dipendente</strong> e usato
                per tutte le sue comunicazioni non ancora inviate.
                {d.status === "blocked" &&
                  " Se era l'unico dato mancante, la comunicazione si sblocca subito."}
              </p>
              <label htmlFor="codice-inps">Codice comunicazione INPS</label>
              <Input
                id="codice-inps"
                autoFocus
                maxLength={30}
                placeholder="Es. 1608226340839731"
                value={codiceInps}
                onChange={(e): void => {
                  setCodiceInps(e.target.value.replace(/\s+/g, ""));
                  setErroreModulo(null);
                }}
                onPressEnter={salvaCodiceInps}
              />
              {erroreModulo && <span className="errore">{erroreModulo}</span>}
              <div className="pulsanti">
                <Button
                  type="primary"
                  loading={salvataggio}
                  onClick={salvaCodiceInps}
                  data-cy="intermittent-inps-save"
                >
                  Salva
                </Button>
                <Button disabled={salvataggio} onClick={chiudiModulo}>
                  Indietro
                </Button>
              </div>
            </section>
          )}

          <Dati
            d={d}
            destinatariXml={destinatariXml}
            onCodiceInps={apriCodiceInps}
            disabilitato={modulo || salvataggio}
          />

          <section>
            <Segmented
              block
              value={scheda}
              onChange={(v): void => setScheda(v as TScheda)}
              options={[
                { label: "Cronologia", value: "cronologia" },
                { label: etichettaTurni(d), value: "turni" },
                { label: "File XML", value: "xml" },
              ]}
            />
            {scheda === "cronologia" && <Cronologia eventi={d.events} />}
            {scheda === "turni" && <TurniCollegati turni={d.shifts} />}
            {scheda === "xml" && <FileXml d={d} />}
          </section>
        </Corpo>
      )}
    </Drawer>
  );
};

const PillolaStatoDrawer = ({ d }: { d: TIntermittentCommunicationDetail }): JSX.Element => (
  <Pillole>
    <PillolaStato stato={d.status} etichetta={d.status_label} />
  </Pillole>
);

// ------------------------------------------------------------------ riquadro dello stato

const ICONA_TONO: Record<TTono, JSX.Element> = {
  rosso: <ExclamationCircleOutlined />,
  ambra: <ExclamationCircleOutlined />,
  blu: <ClockCircleOutlined />,
  azzurro: <InfoCircleOutlined />,
  verde: <CheckCircleOutlined />,
  grigio: <StopOutlined />,
};

/** Stessi testi della produzione (ContextAlert); per gli altri stati il prossimo passo. */
const riquadro = (d: TIntermittentCommunicationDetail): TRiquadro => {
  const h = d.hours_to_auto_cancel ?? hoursFromNow(d.cancel_due_at);
  const inpsMancante = !d.inps_code && d.inps_code_editable;
  const tono = TONO_STATO[d.status] || "grigio";
  switch (d.status) {
    case "scheduled":
      return {
        tono,
        titolo: "In programma",
        testo: moment(d.send_due_at).isAfter(moment()) ? (
          <>
            Partirà in automatico <strong>{inHoursLabel(d.send_due_at)}</strong> (
            {fmtDateTime(d.send_due_at)}). Puoi inviarla subito o escluderla.
          </>
        ) : (
          "Partirà in automatico a breve."
        ),
      };
    case "blocked":
      return {
        tono,
        titolo: "Comunicazione bloccata",
        testo: inpsMancante
          ? `${d.block_reason || "Dati obbligatori mancanti"}. Inserisci il codice INPS: la comunicazione si sblocca subito.`
          : `${d.block_reason || "Dati obbligatori mancanti"}. Completa l'anagrafica del dipendente: si sblocca da sola entro 15 minuti.`,
      };
    case "needs_confirmation":
      return {
        tono,
        titolo: "Turno fuori programmazione",
        testo: `Il turno inizia tra ${hoursFromNow(
          d.shift_start_at,
        )} ore, meno delle 24 previste. Conferma per inviare subito la comunicazione.`,
      };
    case "cancellation_pending":
      return {
        tono,
        titolo: `Annullamento automatico tra ${h} ${h === 1 ? "ora" : "ore"}`,
        testo: `${d.cancellation_note || "Nessuna timbratura valida"}. L'annullamento partirà ${fmtDateTime(
          d.cancel_due_at,
        )}. Se il lavoratore era presente, inserisci la presenza oppure usa «Era presente».`,
      };
    case "expired":
      return {
        tono,
        titolo: "Turno iniziato senza comunicazione",
        testo: `La comunicazione non è partita${
          d.block_reason ? ` (${d.block_reason})` : ""
        }. Se è stata inviata in altro modo, segnala come inviata.`,
      };
    case "failed":
      return {
        tono,
        titolo: "Invio non riuscito",
        testo: d.last_error || "La mail non è partita dopo 3 tentativi.",
      };
    case "uncertain":
      return {
        tono,
        titolo: "Esito dell'invio incerto",
        testo:
          "Il sistema si è interrotto durante l'invio. Controlla nella posta inviata se la mail è partita, poi scegli «Segna come inviata» oppure «Riprova».",
      };
    case "deadline_missed":
      return {
        tono,
        titolo: "Termine di annullamento superato",
        testo: `Il limite di legge era ${fmtDateTime(
          d.legal_cancel_deadline_at,
        )}. Decidi se inviare comunque l'annullamento o segnare il lavoratore come presente.`,
      };
    case "worked":
      return {
        tono,
        titolo: "Presenza verificata",
        testo: d.handled_by
          ? `Segnato come presente da ${d.handled_by.surname} ${d.handled_by.name}: nessun annullamento.`
          : "Il lavoratore ha una timbratura valida: nessuna azione necessaria.",
      };
    case "cancelled":
      return { tono, titolo: "Annullamento inviato", testo: `Annullata ${fmtDateTime(d.cancelled_at)}.` };
    default:
      return { tono, titolo: STATUS_META[d.status]?.label || d.status_label, testo: nextStep(d).text };
  }
};

const Riquadro = ({
  d,
  onCodiceInps,
  disabilitato,
}: {
  d: TIntermittentCommunicationDetail;
  onCodiceInps: () => void;
  disabilitato: boolean;
}): JSX.Element => {
  const r = riquadro(d);
  return (
    <div className={`stato ${r.tono}`} role="status">
      {ICONA_TONO[r.tono]}
      <div>
        <strong>{r.titolo}</strong>
        {r.testo}
        {d.status === "blocked" && !d.inps_code && d.inps_code_editable && (
          <div>
            <Button
              size="small"
              type="primary"
              danger
              disabled={disabilitato}
              onClick={onCodiceInps}
              data-cy="intermittent-inps-add"
            >
              Inserisci codice INPS
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ dati

const Dati = ({
  d,
  destinatariXml,
  onCodiceInps,
  disabilitato,
}: {
  d: TIntermittentCommunicationDetail;
  destinatariXml?: string[];
  onCodiceInps: () => void;
  disabilitato: boolean;
}): JSX.Element => {
  const inviatoA = destinatariInvio(d.events);
  const destinatari = (inviatoA || destinatariXml || []).join(", ");
  return (
    <section>
      <h3>Dati della comunicazione</h3>
      <dl>
        <dt>Dipendente</dt>
        <dd>
          {fullName(d)}
          <PilloleTipologia tipologie={d.worker_types} piccole />
          {d.user?.is_disabled && <span className="pillola grigio piccola">disabilitato</span>}
        </dd>
        <dt>Codice fiscale</dt>
        <dd className="numeri">{d.fiscal_code || <span className="mancante">mancante</span>}</dd>
        <dt>Codice INPS</dt>
        <dd>
          {d.inps_code ? (
            <span className="numeri">{d.inps_code}</span>
          ) : (
            <span className="mancante">mancante</span>
          )}
          {d.inps_code_editable && (
            <Button
              type="link"
              size="small"
              disabled={disabilitato}
              onClick={onCodiceInps}
              data-cy="intermittent-inps-edit"
            >
              {d.inps_code ? "Modifica" : "Aggiungi"}
            </Button>
          )}
        </dd>
        <dt>Sede</dt>
        <dd>{d.offices && d.offices.length ? d.offices.join(", ") : "–"}</dd>
        <dt>{d.sent_at ? "Inviata" : "Invio previsto"}</dt>
        <dd>{fmtDateTime(d.sent_at || d.send_due_at)}</dd>
        <dt>Controllo presenze</dt>
        <dd>{fmtDateTime(d.cancel_alert_at)}</dd>
        <dt>{d.cancelled_at ? "Annullata" : "Annullamento automatico"}</dt>
        <dd>{fmtDateTime(d.cancelled_at || d.cancel_due_at)}</dd>
        <dt>Limite di legge</dt>
        <dd>{fmtDateTime(d.legal_cancel_deadline_at)}</dd>
        <dt>{inviatoA ? "XML inviato a" : "Destinatario XML"}</dt>
        <dd>{destinatari || <span className="mancante">non configurato</span>}</dd>
        {d.handled_by && (
          <>
            <dt>Ultima gestione</dt>
            <dd>
              {d.handled_by.surname} {d.handled_by.name}, {fmtDateTime(d.handled_at)}
              {d.note && <span className="secondario">{d.note}</span>}
            </dd>
          </>
        )}
      </dl>
    </section>
  );
};

/** Indirizzi a cui è partito l'ultimo XML (dal registro eventi), se già inviato. */
const destinatariInvio = (eventi: TIntermittentEvent[]): string[] | null => {
  for (let i = eventi.length - 1; i >= 0; i--) {
    const e = eventi[i];
    if ((e.event === "sent" || e.event === "cancelled") && e.payload && Array.isArray(e.payload.to)) {
      return (e.payload.to as unknown[]).map(String);
    }
  }
  return null;
};

// ------------------------------------------------------------------ cronologia

const TONO_EVENTO = (e: TIntermittentEvent): TTono => {
  if (["sent", "presence_verified", "unblocked"].indexOf(e.event) >= 0) return "verde";
  if (
    ["send_failed", "cancel_failed", "blocked", "expired", "deadline_missed"].indexOf(e.event) >= 0
  ) {
    return "rosso";
  }
  if (["cancellation_pending", "uncertain", "reminder"].indexOf(e.event) >= 0) return "ambra";
  if (
    ["cancelled", "shift_attached", "shift_detached", "send_started", "cancel_started"].indexOf(
      e.event,
    ) >= 0
  ) {
    return "grigio";
  }
  return "blu";
};

/** Testo sotto il titolo dell'evento: niente doppioni, motivi tecnici tradotti. */
const messaggioEvento = (e: TIntermittentEvent, titolo: string): string | null => {
  const turno = e.payload && e.payload.shift_planning_id ? `Turno #${e.payload.shift_planning_id}` : null;
  if (e.event === "shift_detached") {
    const motivo = (e.message || "").split(": ")[1];
    return [turno, motivo ? DETACH_REASONS[motivo] || motivo : null].filter(Boolean).join(" · ") || null;
  }
  if (e.event === "shift_attached") return turno;
  if (e.event === "send_failed" || e.event === "cancel_failed") {
    const tentativo = e.payload && e.payload.attempt ? `Tentativo ${e.payload.attempt}: ` : "";
    return tentativo + (e.message || "");
  }
  if (!e.message || e.message.trim().toLowerCase() === titolo.toLowerCase()) return null;
  return e.message;
};

const Cronologia = ({ eventi }: { eventi: TIntermittentEvent[] }): JSX.Element => {
  if (!eventi.length) return <p className="nota">Nessun evento.</p>;
  return (
    <ol className="eventi">
      {eventi.map(e => {
        const codice = e.payload && typeof e.payload.action === "string" ? e.payload.action : null;
        const azione = codice ? ACTION_META[codice as TIntermittentAction]?.label : null;
        const titolo = EVENT_LABELS[e.event] || e.event;
        const messaggio = messaggioEvento(e, titolo);
        return (
          <li key={e.id}>
            <span className={`punto ${TONO_EVENTO(e)}`} aria-hidden="true" />
            <div className="titolo">
              {titolo}
              {azione ? `: ${azione}` : ""}
            </div>
            <div className="quando">
              {moment(e.created_at).format("DD/MM/YYYY HH:mm")}
              {e.user ? ` · ${e.user.surname} ${e.user.name}` : " · sistema"}
            </div>
            {messaggio && <div className="messaggio">{messaggio}</div>}
          </li>
        );
      })}
    </ol>
  );
};

// ------------------------------------------------------------------ turni collegati

/** "Turni (2)" oppure "Turni (2 + 1 non valido)". */
const etichettaTurni = (d: TIntermittentCommunicationDetail): string => {
  const attivi = d.shifts.filter(s => !s.detached_at).length;
  const scollegati = d.shifts.length - attivi;
  return `Turni (${attivi}${scollegati ? ` + ${scollegati} non valid${scollegati === 1 ? "o" : "i"}` : ""})`;
};

/**
 * Orario del turno; il nome del tipo turno si aggiunge solo se dice qualcosa in più
 * (molti tipi turno si chiamano già "08:00 - 13:30").
 */
const titoloTurno = (s: TIntermittentShiftLink): string => {
  if (!s.shift_type) return "Turno";
  const intervallo = `${s.shift_type.time_from.slice(0, 5)}–${s.shift_type.time_to.slice(0, 5)}`;
  const nome = (s.shift_type.name || "").trim();
  const cifre = (v: string): string => v.replace(/\D/g, "");
  const nomeEOrario =
    !nome ||
    cifre(nome) === cifre(intervallo) ||
    /^\d{1,2}[:.]\d{2}\s*[-–]\s*\d{1,2}[:.]\d{2}$/.test(nome);
  return nomeEOrario ? intervallo : `${nome} · ${intervallo}`;
};

const TurniCollegati = ({ turni }: { turni: TIntermittentShiftLink[] }): JSX.Element => (
  <>
    <p className="nota">
      I turni del giorno coperti da questa comunicazione. Un turno eliminato, spostato o cambiato in
      riposo resta in elenco come «non più valido».
    </p>
    {turni.length === 0 ? (
      <p className="nota">Nessun turno collegato.</p>
    ) : (
      <div className="turni-collegati">
        {[...turni]
          .sort((a, b) => Number(!!a.detached_at) - Number(!!b.detached_at))
          .map(s => (
            <div
              key={s.shift_planning_id}
              className={`turno-collegato${s.detached_at ? " non-valido" : ""}`}
            >
              <span>
                <span className="orario">{titoloTurno(s)}</span>
                {s.office && <span className="attenuato"> · {s.office}</span>}
              </span>
              {s.detached_at ? (
                <span className="pillola grigio piccola">
                  Non più valido:{" "}
                  {DETACH_REASONS[s.detach_reason || ""] || s.detach_reason || "turno modificato"}
                </span>
              ) : (
                <span className="pillola verde piccola">Attivo</span>
              )}
            </div>
          ))}
      </div>
    )}
  </>
);

// ------------------------------------------------------------------ XML

/** Un elemento per riga, per leggere l'XML (il file inviato resta quello originale). */
const xmlLeggibile = (xml: string): string =>
  xml.replace(/\n>/g, ">").replace(/\n\/>/g, "/>").replace(/></g, ">\n<");

const FileXml = ({ d }: { d: TIntermittentCommunicationDetail }): JSX.Element => {
  const [tipo, setTipo] = useState<"send" | "cancel">(d.xml_cancel_filename ? "cancel" : "send");
  const [xml, setXml] = useState<string | null>(null);
  const [caricamento, setCaricamento] = useState(false);

  useEffect(() => {
    let annullata = false;
    setCaricamento(true);
    setXml(null);
    apiIntermittentXml(d.id, tipo)
      .then(testo => {
        if (!annullata) setXml(testo);
      })
      .catch(err => {
        if (!annullata) {
          showNotification("error", "XML", intermittentErrorMessage(err, "Impossibile caricare l'XML."));
        }
      })
      .finally(() => {
        if (!annullata) setCaricamento(false);
      });
    return (): void => {
      annullata = true;
    };
  }, [d.id, d.updated_at, tipo]);

  const salvato = tipo === "cancel" ? d.xml_cancel_filename : d.xml_sent_filename;

  const scarica = (): void => {
    if (!xml) return;
    const nome = salvato || `ANTEPRIMA_${tipo === "cancel" ? "ANNULLAMENTO_" : ""}C${d.id}.xml`;
    saveAs(new Blob([xml], { type: "application/xml;charset=utf-8" }), nome);
  };

  return (
    <>
      <div className="testa-sezione">
        <Segmented
          size="small"
          value={tipo}
          onChange={(v): void => setTipo(v as "send" | "cancel")}
          options={[
            { label: "Chiamata", value: "send" },
            { label: "Annullamento", value: "cancel" },
          ]}
        />
        <Button size="small" icon={<DownloadOutlined />} onClick={scarica} disabled={!xml}>
          Scarica
        </Button>
      </div>
      <div className="file">
        <FileTextOutlined />
        {salvato || "File non ancora inviato"}
        {!salvato && (
          <span
            className="pillola grigio piccola"
            title="Generato dai dati attuali: il file definitivo viene creato al momento dell'invio."
          >
            anteprima
          </span>
        )}
      </div>
      {caricamento ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <pre className="xml">{xmlLeggibile(xml || "")}</pre>
      )}
    </>
  );
};
